import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  useKbDraftDeleteMutation,
  useKbDraftQuery,
  useKbDraftStartMutation,
  useKbTemplateMutation,
} from "@/hooks/useOnboarding";
import { useKbMutation, useKbQuery } from "@/hooks/useKnowledgeBase";
import { useKbDraft, KB_MIN_CHARS, KB_MAX_CHARS } from "@/hooks/useKbDraft";
import { WizardFooter } from "./WizardFooter";

const KbEditor = lazy(() =>
  import("@/components/KbEditor").then((m) => ({ default: m.KbEditor })),
);

/**
 * Step 4 — the knowledge base, drafted by the model from the step-2 material.
 *
 * The draft is a starting point, never the knowledge base: it lands in an editable field
 * and only a deliberate save writes `knowledge_base`. If the knowledge base itself were
 * invented, the agent's "never hallucinate, always answer from the KB" guarantee would be
 * gone at its root — so a human reads it first, every time.
 *
 * Generating one takes ~100 s, longer than the proxy in front of the API tolerates, so it
 * runs as a job on the server and this polls for it. That is also what makes closing the
 * tab or reloading mid-generation free: the draft is stored on the onboarding row, so
 * coming back reads it instead of paying for it again.
 */

function formatGeneratedAt(iso: string): string {
  return new Date(iso).toLocaleString("es-MX", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type Props = {
  /** Path param for the draft endpoint — it is a super-admin resource about a client. */
  clientId: string;
  /**
   * Whether step 2 holds any material. Without it there is nothing for the model to
   * organise — the step was skipped — so the AI draft is off and the empty template is
   * the way in.
   */
  hasContext: boolean;
  onBack: (() => void) | null;
  onDone: () => void;
  onDirtyChange: (dirty: boolean) => void;
  isBusy: boolean;
};

export function StepKnowledge({
  clientId,
  hasContext,
  onBack,
  onDone,
  onDirtyChange,
  isBusy,
}: Props): React.JSX.Element {
  const { data: existing, isPending: loadingExisting } = useKbQuery();
  const job = useKbDraftQuery(clientId, !loadingExisting);
  const startDraft = useKbDraftStartMutation(clientId);
  const clearDraft = useKbDraftDeleteMutation(clientId);
  const loadTemplate = useKbTemplateMutation(clientId);
  const save = useKbMutation();

  // Starts from whatever this client already has — revisiting the step must not look like
  // the knowledge base is empty.
  const kb = useKbDraft();
  // A generated draft replaces the whole document. Rather than reach into the
  // editor — which may not have finished loading when the button is pressed —
  // remount it around the new text: `generation` is what forces that.
  const [generated, setGenerated] = useState<string | null>(null);
  const [generation, setGeneration] = useState(0);
  // Both buttons replace the whole document, so either one asks first when there is text
  // on screen; this remembers which one is waiting for the answer.
  const [confirmReplace, setConfirmReplace] = useState<"generate" | "template" | null>(null);
  // The document already pushed into the editor. Without it a poll would re-push the same
  // text on every refetch, wiping the operator's edits underneath them.
  const applied = useRef<string | null>(null);
  const reportedError = useRef<string | null>(null);

  const value = kb.content;
  const hasContent = value.trim().length > 0;
  const { tooShort, tooLong } = kb;
  const generating = job.data?.status === "running" || startDraft.isPending;
  const busy = isBusy || generating || save.isPending || loadTemplate.isPending;
  // When the editor is showing a draft rather than a saved knowledge base, say so — the
  // operator is one navigation away from losing it, and nothing else on screen distinguishes
  // the two documents.
  const showingDraft =
    job.data?.status === "ready" &&
    job.data.generatedAt !== null &&
    (!existing || job.data.generatedAt > existing.createdAt)
      ? job.data.generatedAt
      : null;

  useEffect(() => {
    onDirtyChange(kb.dirty);
  }, [kb.dirty, onDirtyChange]);

  /**
   * Puts a document in the editor — a draft as it lands, or whatever is already there
   * when the step is revisited.
   *
   * Two places, not one: the editor is uncontrolled (a new `markdown` prop is ignored, so
   * it has to be remounted around the text) and it never reports the document it was
   * mounted with back through `onChange`, so `kb` has to be told separately. Feeding only
   * the editor is what left a full screen of text reading as 0 characters.
   */
  function show(document: string, saved: string): void {
    kb.seed(document, saved);
    setGenerated(document);
    setGeneration((n) => n + 1);
  }

  /**
   * Decides which document the step is showing.
   *
   * A saved knowledge base wins over a draft, because saving is the deliberate act that
   * ends the draft's life. "Wins" is decided by date rather than by mere existence: a draft
   * generated AFTER the last save is a regeneration in progress, and ignoring it would mean
   * a reload mid-generation silently threw the result away.
   *
   * Keyed on the text itself, so a refetch that brings back the same draft — the poll, a
   * window refocus — cannot overwrite what the operator has been editing.
   */
  useEffect(() => {
    if (loadingExisting) return;

    const saved = existing?.content ?? "";
    const draftContent = job.data?.content ?? null;
    const draftAt = job.data?.generatedAt ?? null;
    const document =
      draftContent !== null && draftAt !== null && (!existing || draftAt > existing.createdAt)
        ? draftContent
        : saved;

    if (document === "" || applied.current === document) return;
    applied.current = document;

    if (generated === null && document === saved) {
      // The editor already mounted with this text — it just never told `kb`. Remounting a
      // Lexical editor to hand it what it is already displaying is pure cost.
      kb.seed(document, saved);
      return;
    }
    show(document, saved);
    // `show` is recreated every render and only closes over setters and the hook's own
    // stable callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingExisting, existing, job.data]);

  // A generation that failed while nobody was looking (or that a redeploy killed) has its
  // reason stored, so it is reported once on arrival rather than lost with the request.
  useEffect(() => {
    const draft = job.data;
    if (draft?.status !== "failed" || !draft.error) return;
    if (reportedError.current === draft.error) return;
    reportedError.current = draft.error;
    toast.error(draft.error);
  }, [job.data]);

  function handleGenerate(): void {
    // Regenerating throws away whatever is on screen, which may include edits.
    if (hasContent && confirmReplace !== "generate") {
      setConfirmReplace("generate");
      return;
    }
    setConfirmReplace(null);
    reportedError.current = null;
    startDraft.mutate();
  }

  /** Puts the segment's empty template in the editor. Nothing is saved until «Guardar». */
  function handleTemplate(): void {
    if (hasContent && confirmReplace !== "template") {
      setConfirmReplace("template");
      return;
    }
    setConfirmReplace(null);
    loadTemplate.mutate(undefined, {
      onSuccess: ({ content }) => show(content, existing?.content ?? ""),
    });
  }

  async function handleSave(): Promise<void> {
    if (kb.invalid) return;
    await save.mutateAsync({ content: value });
    kb.markSaved();
    // The knowledge base now exists, so the draft has done its job. Best-effort: the
    // precedence rule above keeps the step correct even if this never lands.
    clearDraft.mutate();
    onDirtyChange(false);
    onDone();
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[13px] text-ink-500">
        El borrador se arma con el material del paso Contexto y la plantilla del tipo de
        cliente. Lo que el material no diga queda marcado como{" "}
        <code className="rounded bg-ink-050 px-1 font-mono text-[12px]">
          [PENDIENTE: …]
        </code>{" "}
        para que se vea el hueco en lugar de inventarlo.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleGenerate}
          disabled={busy || loadingExisting || !hasContext}
          className="flex items-center gap-2 rounded-lg bg-moss px-4 py-2 text-sm font-semibold text-white hover:bg-moss/90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Sparkles size={14} />
          {generating
            ? "Generando…"
            : hasContent
              ? "Regenerar borrador"
              : "Generar borrador con IA"}
        </button>
        <button
          type="button"
          onClick={handleTemplate}
          disabled={busy || loadingExisting}
          className="flex items-center gap-2 rounded-lg border border-ink-200 px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-050 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <FileText size={14} />
          Empezar con la plantilla vacía
        </button>
        {!hasContext && !generating && (
          <span className="text-xs text-ink-500">
            Sin material en el paso Contexto no hay borrador con IA. Pega material ahí, o
            llena la plantilla a mano.
          </span>
        )}
        {generating && (
          <span className="text-xs text-ink-500">
            Tarda un par de minutos — está redactando el documento completo. Puedes cerrar
            esta página: el borrador se guarda solo y aparece aquí cuando vuelvas.
          </span>
        )}
      </div>

      {confirmReplace && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
          <span className="mr-auto">
            {confirmReplace === "generate" ? "Regenerar" : "Cargar la plantilla"} reemplaza
            el texto de abajo, incluidas tus ediciones.
          </span>
          <button
            type="button"
            onClick={() => setConfirmReplace(null)}
            className="font-medium hover:underline"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={confirmReplace === "generate" ? handleGenerate : handleTemplate}
            className="rounded-md bg-amber-200 px-2 py-1 font-semibold hover:bg-amber-300"
          >
            {confirmReplace === "generate" ? "Regenerar" : "Reemplazar"}
          </button>
        </div>
      )}

      {showingDraft && (
        <p className="text-[12px] text-ink-500">
          Borrador generado el {formatGeneratedAt(showingDraft)} — todavía sin guardar.
        </p>
      )}

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-ink-700">Base de conocimiento</label>
        <div
          className={`flex h-[28rem] flex-col overflow-hidden rounded-md border bg-white ${
            tooLong || kb.problem ? "border-red-400" : "border-ink-200"
          } ${busy ? "opacity-50" : ""}`}
        >
          {loadingExisting ? (
            <div className="flex-1 space-y-3 p-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-4 animate-pulse rounded bg-ink-100"
                  style={{ width: `${70 - i * 10}%` }}
                />
              ))}
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto">
            <Suspense fallback={<div className="flex-1" />}>
              <KbEditor
                key={generation}
                markdown={generated ?? existing?.content ?? ""}
                onChange={kb.onEditorChange}
                readOnly={busy}
                placeholder="Genera el borrador con IA, empieza con la plantilla vacía o escríbela a mano."
              />
            </Suspense>
            </div>
          )}
        </div>
        <div className="flex items-start justify-between gap-3">
          <span className="text-xs text-ink-400">
            {kb.problem ??
              "Revisa los datos antes de guardar: esto es lo que el agente va a citar como verdad."}
          </span>
          <span
            className={`shrink-0 text-xs ${tooLong ? "text-red-500" : "text-ink-300"}`}
          >
            {value.length.toLocaleString("es-MX")}/{KB_MAX_CHARS.toLocaleString("es-MX")}
          </span>
        </div>
      </div>

      <WizardFooter
        onBack={onBack}
        onContinue={() => void handleSave()}
        canContinue={!kb.invalid}
        isBusy={busy}
        continueLabel="Guardar y continuar"
        hint={
          tooLong
            ? "El documento excede el límite."
            : tooShort
              ? `Mínimo ${KB_MIN_CHARS} caracteres.`
              : undefined
        }
      />
    </div>
  );
}
